import { CognitoUser } from "amazon-cognito-identity-js";
import Amplify, { Auth } from "aws-amplify";
import axios, { AxiosResponse } from "axios";
import { ethers } from "ethers";
import React, { useEffect, useState } from "react";
import { User, UserDetailResponse } from "../common/models/User";
import { AuthContext } from "../hooks/useAuth";
import UserPool from "../UserPool";
import { getSelectedProvider, sleep } from "../utils";

interface NewUserResponse {
  isNewUser: boolean;
}

export const AuthProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  // const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const signin = async (
    publicAddress: string,
    successCallback: (user: User) => void,
    errorCallback: (errorMessage: string) => void
  ) => {
    setupCognito();
    requestSignature(publicAddress, successCallback, errorCallback);
  };

  const requestSignature = async (account, successCallback, errorCallback) => {
    // sign message and get the access token
    const cognitoUser = await handleAmplifySignIn(account);
    const messageToSign = cognitoUser.challengeParam.message;

    const provider = new ethers.providers.Web3Provider(getSelectedProvider());
    const data = ethers.utils.toUtf8Bytes(messageToSign);
    const signature = await provider.send("personal_sign", [
      ethers.utils.hexlify(data),
      account.toLowerCase(),
    ]);

    await Auth.sendCustomChallengeAnswer(cognitoUser, signature);
    await checkUser(successCallback, errorCallback);
  };

  const checkUser = async (successCallback, errorCallback) => {
    try {
      const currentAuthenticatedUser: CognitoUser =
        await Auth.currentAuthenticatedUser();
      const publicAddress = currentAuthenticatedUser.getUsername();

      const isUserNew = await isNewUser(publicAddress);
      if (isUserNew) {
        createNewUser(publicAddress, successCallback, errorCallback, currentAuthenticatedUser);
      } else {
        queryUserDetails(publicAddress, successCallback, errorCallback);
      }
      setIsAdmin(
        publicAddress.toLowerCase() ===
          process.env.REACT_APP_COMPOSENFT_SERVICER.toLowerCase()
      );
    } catch (err) {
      console.error("checkUser error", err);
    }
  };

  const setupCognito = () => {
    const cognitoPoolId = process.env.REACT_APP_COGNITO_POOL_ID;
    const cognitoWebClientId = process.env.REACT_APP_COGNITO_WEB_CLIENT_ID;
    const region = process.env.REACT_APP_COGNITO_REGION;

    Amplify.configure({
      Auth: {
        region,
        userPoolId: cognitoPoolId,
        userPoolWebClientId: cognitoWebClientId,
        authenticationFlowType: "CUSTOM_AUTH",
      },
    });
  };

  const handleAmplifySignIn = async (address) => {
    try {
      const cognitoUser = await Auth.signIn(address);
      return cognitoUser;
    } catch (err) {
      /*Cognito doesn't give us a lot of flexibility on error responses
      so we'll have to string match our 'User Not Found' error here
      and create a cognito user with the address as their username if they don't exist*/
      if (err && err.message && err.message.includes("[404]")) {
        const params = {
          username: address,
          password: getRandomString(30),
        };
        await Auth.signUp(params);
        return handleAmplifySignIn(address);
      } else {
        throw err;
      }
    }
  };

  const getRandomString = (bytes) => {
    const randomValues = new Uint8Array(bytes);
    window.crypto.getRandomValues(randomValues);
    return Array.from(randomValues).map(intToHex).join("");
  };

  const intToHex = (nr) => {
    return nr.toString(16).padStart(2, "0");
  };

  useEffect(() => {
    const userObject = window.localStorage.getItem("user");
    if (userObject) {
      const parsed = JSON.parse(userObject) as User;
      setUser(parsed);
      setIsAdmin(
        parsed.wallet_address.toLowerCase() ===
          process.env.REACT_APP_COMPOSENFT_SERVICER.toLowerCase()
      );
    }
  }, []);

  const isNewUser = async (publicAddress: string) => {
    return axios
      .get<NewUserResponse>(
        process.env.REACT_APP_BACKEND_URL + "/is_new_user?wallet_address=" + publicAddress
      )
      .then((response: AxiosResponse<NewUserResponse>) => {
        return response.data.isNewUser;
      })
      .catch((response: NewUserResponse) => {
        return false;
      });
  };

  const createNewUser = async (
    publicAddress: string,
    callback: (user: User) => void,
    errorCallback: (errorMessage: string) => void,
    currentAuthenticatedUser: CognitoUser
  ) => {
    const formData = new FormData();
    formData.append("wallet_address", publicAddress);
    axios
      .post<UserDetailResponse>(
        process.env.REACT_APP_BACKEND_URL + "/create_new_user",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": currentAuthenticatedUser.getSignInUserSession().getIdToken().getJwtToken()
          },
        }
      )
      .then((response: AxiosResponse<UserDetailResponse>) => {
        const user = response.data.userDetails as User;
        window.localStorage.setItem("user", JSON.stringify(user));
        setUser(user);
        callback(user);
      })
      .catch((error) => {
        errorCallback(error.toString());
      });
  };

  const queryUserDetails = async (
    publicAddress: string,
    callback: (user: User) => void,
    errorCallback: (errorMessage: string) => void
  ) => {
    axios
      .get<UserDetailResponse>(
        process.env.REACT_APP_BACKEND_URL +
          "/get_user_details?wallet_address=" +
          publicAddress
      )
      .then((response: AxiosResponse<UserDetailResponse>) => {
        const user = response.data.userDetails as User;
        window.localStorage.setItem("user", JSON.stringify(user));
        setUser(user);
        callback(user);
      })
      .catch((error) => {
        errorCallback(error.toString());
      });
  };

  const signout = async (callback: () => void) => {
    await Auth.signOut();
    window.localStorage.removeItem("user");
    setUser(null);
    setIsAdmin(false);
    callback();
  };

  const refreshUser = (callback: (user: User) => void) => {
    if (user) {
      axios
        .get<UserDetailResponse>(
          process.env.REACT_APP_BACKEND_URL +
            "/get_user_details?wallet_address=" +
            user.wallet_address
        )
        .then((response: AxiosResponse<UserDetailResponse>) => {
          const user = response.data.userDetails as User;
          window.localStorage.setItem("user", JSON.stringify(user));
          setUser(user);
          callback(user);
        });
    }
  };

  const value = {
    user,
    signin,
    signout,
    refreshUser,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
